import { defineStyleConfig, defineStyle } from '@chakra-ui/react'
import { mode, transparentize } from '@chakra-ui/theme-tools'
import { textStyles } from '../foundations/textStyles'

export const variantGhost = defineStyle(props => {
  const { colorScheme, theme } = props

  if (colorScheme === 'secondary') {
    return {
      color: mode(`inherit`, `whiteAlpha.900`)(props),
      _hover: {
        bg: mode(`neutral.100`, `whiteAlpha.200`)(props),
        color: mode(`black`, `white`)(props),
      },
      _active: { bg: mode(`neutral.200`, `whiteAlpha.300`)(props) },
    }
  }

  const darkHoverBg = transparentize(`${colorScheme}.600`, 0.12)(theme)
  const darkActiveBg = transparentize(`${colorScheme}.600`, 0.24)(theme)

  return {
    color: mode(`${colorScheme}.600`, `${colorScheme}.700`)(props),
    bg: 'transparent',
    _hover: {
      bg: mode(`${colorScheme}.100`, darkHoverBg)(props),
      color: mode(`${colorScheme}.700`, `${colorScheme}.800`)(props),
    },
    _active: {
      bg: mode(`${colorScheme}.200`, darkActiveBg)(props),
    },
  }
})

export const variantOutline = defineStyle(props => {
  const { colorScheme: c } = props
  const borderColor = mode(`blackAlpha.300`, `whiteAlpha.300`)(props)
  return {
    border: '1px solid',
    borderColor: c === 'secondary' ? borderColor : 'currentColor',
    '.chakra-button__group[data-attached][data-orientation=horizontal] > &:not(:last-of-type)':
      { marginEnd: '-1px' },
    '.chakra-button__group[data-attached][data-orientation=vertical] > &:not(:last-of-type)':
      { marginBottom: '-1px' },
    ...variantGhost(props),
  }
})

export const variantSolid = defineStyle(props => {
  const { colorScheme } = props
  const borderColor = mode(`blackAlpha.200`, `whiteAlpha.200`)(props)

  if (colorScheme === 'secondary') {
    const bg = mode(`blackAlpha.50`, `whiteAlpha.100`)(props)

    return {
      bg,
      border: '1px solid',
      borderColor,
      color: mode('blue.500', 'blue.800')(props),
      _hover: {
        bg: mode(`blackAlpha.100`, `whiteAlpha.200`)(props),
        _disabled: {
          bg,
        },
      },
      _active: {
        bg: mode(`blackAlpha.200`, `whiteAlpha.300`)(props),
      },
    }
  }

  const background = `${colorScheme}.500`

  return {
    bg: background,
    color: mode(`white`, `black`)(props),
    border: '1px solid',
    borderColor,
    _hover: {
      bg: `${colorScheme}.600`,
      _disabled: {
        bg: background,
      },
    },
    _active: { bg: `${colorScheme}.700` },
  }
})

export const variantLink = defineStyle(props => {
  const { colorScheme } = props

  const overrides = {
    secondary: {
      color: mode(`neutral.800`, `neutral.900`)(props),
      activeColor: mode(`neutral.900`, `white`)(props),
    },
  }

  const {
    color = mode(`${colorScheme}.600`, `${colorScheme}.700`)(props),
    activeColor = mode(`${colorScheme}.700`, `${colorScheme}.800`)(props),
  } = overrides[colorScheme] ?? {}

  return {
    padding: 0,
    height: 'auto',
    lineHeight: 'normal',
    verticalAlign: 'baseline',
    color,
    _hover: {
      textDecoration: 'underline',
      _disabled: {
        textDecoration: 'none',
      },
    },
    _active: {
      color: activeColor,
    },
  }
})

export const variants = {
  solid: variantSolid,
  ghost: variantGhost,
  outline: variantOutline,
  link: variantLink,
}

export const sizes = {
  xs: defineStyle({
    ...textStyles.xs,
  }),
  sm: defineStyle({
    ...textStyles.sm,
  }),
  md: defineStyle({
    ...textStyles.md,
  }),
  lg: defineStyle({
    ...textStyles.lg,
  }),
}

export const Button = defineStyleConfig({
  variants,
  sizes,
  defaultProps: {
    variant: 'solid',
    size: 'md',
    colorScheme: 'secondary',
  },
})
